import { Link } from 'react-router-dom'
import { formatDayTime, formatMoney, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import type { BoardCard } from './api'
import {
  nextSteps,
  pickedCount,
  pointsPayText,
  shipText,
  shipWhere,
  slaView,
  stageAction,
  type Step,
} from './model'

export interface OrderCardProps {
  card: BoardCard
  assigneeName: string
  canUpdate: boolean
  dragging: boolean
  /** Кнопка этапа: следующий шаг, если он один и очевиден. */
  onAdvance: (step: Step) => void
  /** «Сменить статус» — где шаг надо выбрать в диалоге. */
  onMove: () => void
  onDragStart: () => void
  onDragEnd: () => void
}

/**
 * Карточка на доске — в порядке чтения кита: что горит (таймер этапа), как
 * получают, что за заказ и на сколько, кому, куда, до какого срока бронь, кто
 * собирает, что сделать.
 *
 * Таймер этапа приходит с сервера (`sla`): он считает его по настройкам сети
 * и по часам аптеки, поэтому «просрочен» на экране и в отчётах — одно и то же.
 *
 * Открыть заказ — нажатием на карточку: номер — ссылка, растянутая на всю
 * карточку (`orders.css`). Так работают и клавиатура, и «открыть в новой
 * вкладке», а кнопка этапа лежит поверх ссылки.
 */
export function OrderCard({
  card,
  assigneeName,
  canUpdate,
  dragging,
  onAdvance,
  onMove,
  onDragStart,
  onDragEnd,
}: OrderCardProps) {
  const delivery = card.ship === 'delivery'
  const sla = slaView(card.sla)
  const picked = pickedCount(card)
  const action = stageAction(card)
  // Кнопка «Сменить статус» — только если переводить есть куда.
  const choose = !action && nextSteps(card).length > 0
  const where = shipWhere(card)
  const points = pointsPayText(card)

  return (
    <article
      className="ui-ocard"
      aria-label={`Заказ № ${card.number}`}
      data-late={sla?.state === 'late' || undefined}
      data-dragging={dragging || undefined}
      draggable={canUpdate}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="top">
        {sla ? (
          <span className="ui-sla" data-state={sla.state}>
            <Icon name={sla.state === 'late' ? 'i-alert' : 'i-clock'} size={14} />
            {sla.text}
          </span>
        ) : null}
        <span className="ui-method" data-method={card.ship}>
          <Icon name={delivery ? 'i-truck' : 'i-pin'} size={14} />
          {shipText(card.ship)}
        </span>
      </div>
      <div className="id">
        <b>
          {/* Ссылку не тянем: перетаскивается карточка, а не адрес заказа. */}
          <Link
            to={`/orders/${card.id}`}
            className="ord-open"
            aria-label={`Открыть заказ № ${card.number}`}
            draggable={false}
          >
            <span>№</span>
            {card.number}
          </Link>
        </b>
        <span className="sum">{formatMoney(card.total)}</span>
      </div>
      <div className="who">
        {card.customerName || '—'} ·{' '}
        <span className="ui-hint">
          {withCountRu(card.lines.length, ['позиция', 'позиции', 'позиций'])}
        </span>
      </div>
      <div className="meta">
        <Icon name={delivery ? 'i-truck' : 'i-pin'} size={14} />
        <span>{where || 'аптека не указана'}</span>
      </div>
      {points ? (
        <div className="meta">
          <Icon name="i-coins" size={14} />
          <span>{points}</span>
        </div>
      ) : null}
      {/* Готовый заказ с оплатой в аптеке ждёт покупателя до срока брони (R-39). */}
      {card.status === 'ready_for_pickup' && card.reservationExpires ? (
        <div className="meta">
          <Icon name="i-clock" size={14} />
          <span>Бронь до {formatDayTime(card.reservationExpires)}</span>
        </div>
      ) : null}
      <div className="meta asg">
        {assigneeName ? (
          <span className="ui-avatar" data-size="sm" aria-hidden="true">
            {assigneeName.slice(0, 1)}
          </span>
        ) : (
          <span className="ui-avatar" data-size="sm" data-empty aria-hidden="true">
            ?
          </span>
        )}
        <span>{assigneeName || 'Сборщик не назначен'}</span>
      </div>
      {card.status === 'assembling' && card.lines.length ? (
        <div className="ui-progress">
          <span
            className="bar"
            role="progressbar"
            aria-label={`Собрано позиций: ${picked} из ${card.lines.length}`}
            aria-valuenow={picked}
            aria-valuemax={card.lines.length}
          >
            <i style={{ width: `${Math.round((picked / card.lines.length) * 100)}%` }} />
          </span>
          {picked} из {card.lines.length}
        </div>
      ) : null}
      {canUpdate && (action || choose) ? (
        <div className="foot">
          {action ? (
            <button
              type="button"
              className="ui-btn"
              data-size="sm"
              onClick={() => {
                onAdvance(action)
              }}
            >
              {action.action}
              <span className="visually-hidden"> — заказ № {card.number}</span>
            </button>
          ) : (
            <button
              type="button"
              className="ui-btn"
              data-size="sm"
              data-variant="secondary"
              onClick={onMove}
            >
              Сменить статус<span className="visually-hidden"> заказа № {card.number}</span>
            </button>
          )}
        </div>
      ) : null}
    </article>
  )
}
